/**
 * `AW10` — pantalla «Envíos» de F2.
 *
 * Tabla de envíos con las siete columnas del PRD, estado vacío, paginado,
 * *Ver detalle* y *Cancelar*. Habla con `admin-web` a través del borde
 * inyectado por contexto; no guarda estado ni credenciales.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * LOS GUARDS DE LA ETAPA QUE VIVEN ACÁ
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ② estados rotulados (`ETIQUETAS_DE_ESTADO`) y el motivo del fallo debajo.
 * ③ `completado` NO se pinta como éxito: los fallidos se ven en Conteos.
 * ④ Cancelar sólo en `programado`; el desenlace negativo dice CUÁL pasó.
 * ⑤ sin cacheo: cada página y cada detalle se piden frescos.
 * ⑥ tres estados, no dos: sin envíos / cargando / no se pudo leer.
 */

import { useEffect, useState } from "react"
import type { JSX } from "react"
import { useNavigate } from "react-router"
import { useBordeEnvios } from "../contexto-borde-envios.tsx"
/*
 * ⛔⛔ `R2-UI3` · `guard` 5 + CA-82 · **el paginado de Envíos usa 50 fijos, y NO
 * el tamaño de página de la configuración.** Por eso acá ya no entra
 * `useConfiguracion`: el tamaño de página era su ÚNICO uso en esta pantalla, y
 * dejarlo «por las dudas» era dejar una puerta por la que el 50 se vuelve a atar
 * a la configuración de otra pantalla. El gate `R2-UI3` lo caza textualmente.
 */
import { Paginado } from "../paginado.tsx"
// ⛔ SIN la extensión `.ts`: el gate `R2-UI3` (chequeo `fechas-chile`) exige el
// import terminado en `formato-fecha` para probar que se usa el formateador
// COMPARTIDO que B1 copió a `src/` (RT 21: un solo formateador, no dos que
// divergen). `moduleResolution: "bundler"` resuelve la ruta sin extensión igual
// que `.ts` (tsconfig de la app, verificado ahí), así que no cambia el build.
import { formatearFecha } from "../formato-fecha"
import { FormularioEnvio } from "./formulario.tsx"
import type { CargarElegibles, CargarListaParaEnvios } from "./formulario.tsx"
import { SIN_CATALOGO_DE_PLANTILLAS } from "../plantillas/borde-plantillas.ts"
// ⛔ `CargarListaParaEnvios` se importaba de `borde-plantillas.ts`, que NO lo
// exporta: ese módulo exporta `CargarPlantillas` (el catálogo crudo) y la lista
// es un tipo de `formulario.tsx`, que es quien la consume. `tsc` lo marcaba
// `TS2305` («no exported member»), y al tipar el símbolo como `any` arrastraba
// la comprobación de todo lo que colgaba de él.
import type { CargarPlantillas } from "../plantillas/borde-plantillas.ts"
import type {
  DispatchDetail,
  DispatchListPage,
  DispatchRow,
} from "@contracts/dispatch-api/index.ts"
import type { CancelOutcome, DispatchId, DispatchState } from "@contracts/dispatch/index.ts"
import { useBrazo } from "../brazo.ts"
import { Dialogo } from "../banco-de-contenido/dialogo.tsx"
import { esBrazo, selloDelBrazo } from "../zonas.ts"
import type { Brazo } from "../zonas.ts"

const SIN_FUENTE_DE_AUDIENCIA: CargarElegibles = () =>
  Promise.resolve({ filas: [], total: 0 })

const COLUMNAS = [
  "Nombre del envío",
  "Plantilla",
  "Audiencia",
  "Programación",
  "Estado",
  "Conteos",
  "Acciones",
] as const

/**
 * ⛔ `guard` 5 · CA-82 (`PRD_R2.md:1836`) · **50 por página, y es una constante de
 * LA PANTALLA.**
 *
 * Hasta `R2-UI3` el tamaño salía de la configuración de OTRA pantalla, así que
 * ajustar aquélla movía los envíos de acá sin que nada lo dijera. El criterio
 * fija 50 y el `guard` 5 prohíbe volver a atarlo a la configuración.
 */
const ENVIOS_POR_PAGINA = 50

/**
 * ⛔ `R2-UI3` · CA-82 · **la barra de paginado VISIBLE la dibuja la primitiva
 * `src/paginado.tsx`** (`AW31`), que es la ÚNICA definición de paginado del
 * sistema. Por eso esta pantalla declara en texto —y no repitiendo botones— que su
 * lista se recorre con esos dos controles: es la instrucción que necesita quien
 * navega con lector de pantalla, y evita que «Anterior»/«Siguiente» existan DOS
 * veces para la misma lista. El estilo va EN LÍNEA a propósito: no depende de que
 * `estilos.css` tenga (o no) una clase de ocultamiento visual.
 */
const SOLO_PARA_LECTORES = {
  position: "absolute" as const,
  width: "1px",
  height: "1px",
  padding: 0,
  margin: "-1px",
  overflow: "hidden" as const,
  clip: "rect(0, 0, 0, 0)",
  whiteSpace: "nowrap" as const,
  border: 0,
}

/**
 * ⛔ `guard` ② + CA-17…20 · **el estado se traduce acá; el motivo llega YA
 * traducido del servidor.**
 *
 * La celda Estado NO es `dispatch.state` literal (`R2-UI3` deroga esa mitad de
 * `R1.1`): `programado`/`enviando`/… son las palabras del CONTRATO, y la pantalla
 * las rotula. ⛔ Y no hay tabla de códigos: `RT 8` manda mostrar el
 * `failure.reason` que el servidor ya tradujo, no volver a mapear `131031`.
 */
const ETIQUETAS_DE_ESTADO: Record<DispatchState, string> = {
  programado: "Programado",
  enviando: "Enviando",
  completado: "Completado",
  cancelado: "Cancelado",
  fallido: "Fallido",
}

function etiquetaDeEstado(state: DispatchState): string {
  return ETIQUETAS_DE_ESTADO[state]
}

/**
 * CA-80 · **el motivo va DEBAJO del estado y sale del `failure.errorText` del
 * borde**: el servidor es el único que traduce (`R2-AW3`/`B37`). Un `fallo` sin
 * motivo es un envío que el operador no puede explicar.
 */
function motivoDelFallo(dispatch: DispatchRow["dispatch"]): string | null {
  return dispatch.failure === null ? null : dispatch.failure.errorText
}

/**
 * ⛔ `guard` B37/`RT 21` · **la fecha la formatea `formatearFecha`, no esta
 * pantalla.**
 *
 * Acá vivía un `Intl.DateTimeFormat` propio con su `FORMATO_FECHA`, una de las
 * copias que `B37` retira: fijaba la zona de nuevo y se desincronizaba del
 * formateador compartido (`../formato-fecha.ts`), que es el que dice
 * `dd-mm-aaaa hh:mm` en hora de Chile.
 */
function textoDeProgramación(scheduledFor: Date | null): string {
  return scheduledFor === null ? "Inmediato" : formatearFecha(scheduledFor)
}

/**
 * CA-11/12 · **los CUATRO conteos del contrato, y en este orden.**
 *
 * ⚠️ `leidos` es acumulativo (⟦los-conteos-son-acumulativos⟧): «enviados» ya
 * incluye a los entregados y a los leídos, así que los cuatro NO son casillas
 * excluyentes y la línea no se puede reordenar sin mentir.
 */
function textoDeConteos(counts: DispatchRow["counts"]): string {
  return `${counts.enviados} enviados · ${counts.entregados} entregados · ${counts.leidos} leídos · ${counts.fallidos} fallidos`
}

/**
 * ⛔ `guard` ④ + ⟦cancel-resuelve⟧ · **el `409` de cancelar NO significa «no
 * existe».**
 *
 * `POST envios/{id}/cancelacion` contesta `409` cuando el envío ya no está
 * `programado` (`cancelRejected`, `contracts/dispatch-api/http.ts:223`), y el
 * CUERPO es un `CancelOutcome` que dice CUÁL estado lo impidió. Sin leer el
 * cuerpo (`.json()`) la pantalla sólo puede decir «no se pudo», que es lo que el
 * `guard` 4 prohíbe. El borde deja la respuesta del `409` SIN CONSUMIR justo para
 * que se pueda leer acá (`borde-envios.ts`, `cancelar`).
 *
 * ⚠️ El `404` es el ÚNICO desenlace que significa «ese envío no existe». Antes
 * CUALQUIER error caía en esa frase, así que un `502` o una sesión vencida se
 * reportaban como un envío borrado.
 */
/**
 * `R2-UI18` · ADR-0374 §1 — la fila cuyo `dispatch.id` es `id` pasa a ser la que el
 * detalle acaba de traer (estado, motivo y conteos: `dispatch` y `counts` enteros).
 * Las demás quedan intactas; si ninguna tiene ese id, la lista vuelve tal cual (no se
 * agrega nada). Se guarda como `DispatchRow` —sin los desgloses del detalle— porque la
 * lista no los muestra.
 */
function conLaFilaDelDetalle(
  filas: readonly DispatchRow[],
  id: DispatchRow["dispatch"]["id"],
  detalle: DispatchRow,
): readonly DispatchRow[] {
  if (!filas.some((fila) => fila.dispatch.id === id)) return filas
  return filas.map((fila) =>
    fila.dispatch.id === id ? { dispatch: detalle.dispatch, counts: detalle.counts } : fila,
  )
}

function esErrorConStatus(causa: unknown, status: number): causa is Error & { statusCode: number } {
  return (
    causa instanceof Error &&
    "statusCode" in causa &&
    typeof causa.statusCode === "number" &&
    causa.statusCode === status
  )
}

/**
 * Valida la forma mínima del cuerpo del `409` antes de usarla: un `as` no sería
 * una comprobación. El lector canónico vive en el borde, pero el borde NO puede
 * usarlo acá —dejaría el cuerpo consumido y el estado perdido—.
 */
function esCancelOutcome(valor: unknown): valor is CancelOutcome {
  if (typeof valor !== "object" || valor === null) return false
  const { cancelled, arm, state } = valor as Record<string, unknown>
  if (typeof arm !== "string") return false
  if (cancelled === true) return true
  return cancelled === false && typeof state === "string"
}

async function outcomeDelError(causa: unknown): Promise<CancelOutcome | null> {
  if (!esErrorConStatus(causa, 409)) return null
  if (!("respuesta" in causa)) return null
  const respuesta = causa.respuesta
  if (!(respuesta instanceof Response)) return null
  const crudo: unknown = await respuesta.json()
  return esCancelOutcome(crudo) ? crudo : null
}

/*
 * ⛔⭐ **RASTRO · 24/9/2026, `R2-UI11`.** Acá decía `PantallaEnvios(props?: {…})`.
 * Con `listaParaEnvios` y `lotesDe` obligatorias, un PARÁMETRO opcional dejaba
 * abierta la misma puerta por otro lado: `PantallaEnvios()` llamado como función
 * compilaba sin ninguna de las dos. Se quita el `?` del parámetro; los `props?.`
 * que quedan adentro siguen compilando y no cambian nada.
 */
export function PantallaEnvios(props: {
  readonly cargarElegibles?: CargarElegibles
  /**
   * ⭐ `AW46` · El catálogo de plantillas del brazo para el selector del
   * formulario. Se pasa DERECHO, sin tocarlo: esta pantalla no filtra por estado
   * ~~—eso lo hace el selector, a la vista—~~ ni resuelve el brazo, que lo resuelve
   * `App.tsx` desde la ruta.
   *
   * ⛔⭐ **RASTRO · 24/9/2026, `R2-UI12`.** El selector tampoco filtra por estado:
   * ofrece la lista «Plantillas para envíos» ENTERA, aprobadas o no
   * (`PRD_R2.md:1396`, `:1464`), y quien decide si una se puede usar es el motor
   * al ejecutar. Ninguna capa de esta pantalla filtra por `sendability`.
   */
  readonly cargarPlantillas?: CargarPlantillas
  /**
   * ⭐ `R2-UI2` · ~~La lista de plantillas APROBADAS para el selector del
   * formulario, tal como la arma `plantillas/borde-plantillas.ts`. Es opcional
   * para que `App.tsx` la agregue en su único hunk admisible sin romper el
   * montaje suelto de las pruebas; si falta, el formulario lo dice y no ofrece
   * ninguna.~~
   *
   * ⛔⭐ **RASTRO · 24/9/2026, `R2-UI11` (hallazgo `R2-H-023`).** Las dos
   * afirmaciones tachadas dejaron de ser ciertas:
   *
   *   · **no es la lista de las APROBADAS**: es la lista «Plantillas para envíos»
   *     del brazo ENTERA (`PRD_R2.md:1396`, «aprobadas o no»), tal como la cruza
   *     `BordePlantillas.listaParaEnvios`. Qué se OFRECE de esa lista lo decide el
   *     formulario —hoy descarta las no aprobadas, y eso lo corrige `R2-UI12`—;
   *     esta pantalla la pasa derecho y no filtra.
   *   · **no es opcional**: opcional fue exactamente lo que dejó a `tsc`, a las
   *     pruebas de esta pantalla y al examen de `R2-UI2` en verde con el selector
   *     vacío, porque `App.tsx` nunca la pasó y nada lo notó. Desde `R2-UI11` un
   *     montaje que la omita no compila. La arma `App.tsx` con el brazo de la
   *     DIRECCIÓN (`useListaParaEnvios`); sin brazo, la fuente RECHAZA nombrando
   *     la causa, y el formulario lo dice sin ofrecer nada. Los montajes sueltos
   *     de `pruebas/**` pasan una fuente declarada con nombre.
   */
  readonly listaParaEnvios: CargarListaParaEnvios
  /**
   * ⭐ `R2-UI2` · Los lotes del brazo para el filtro de audiencia. ~~Misma razón
   * que `listaParaEnvios`: entra por prop y es opcional.~~
   *
   * ⛔⭐ **RASTRO · 24/9/2026, `R2-UI11`.** Sigue entrando por prop, pero ya **no
   * es opcional**, por lo mismo que `listaParaEnvios`: sin ella el `<fieldset>`
   * «Filtrar por lote» no se dibujaba nunca y nada lo marcaba. `App.tsx` la arma
   * sobre `bordePadron.lotes` —la MISMA función que usan Participantes y
   * Exportación—, no sobre un segundo `fetch`.
   */
  readonly lotesDe: () => Promise<readonly string[]>
  /**
   * ⭐ `R2-UI2` · «Nuevo envío» como **pantalla propia**: la DIRECCIÓN gobierna la
   * vista. `#/brazos/<brazo>/envios` es la lista;`#/brazos/<brazo>/envios/nuevo`
   * es el formulario, y `App.tsx` monta un `<Route>` para cada una pasando este
   * prop.
   *
   * ⛔⛔ Entra por PROP y **no leyendo la dirección con `useLocation()`**, y eso lo
   * decidió un rojo: `useLocation()` **lanza fuera de un enrutador**, y las once
   * pruebas de `aw10-envios.test.tsx` montan esta pantalla suelta, con sus
   * contextos a mano. Medido: cuatro se pusieron rojas de golpe, **por el
   * instrumento y no por el sujeto**.
   *
   * ⚠️ No es lo mismo que `useBrazo()`, que sí lee la ruta: `useParams()` fuera de
   * un enrutador **no lanza** —devuelve `{}`— y por eso puede hacerlo (ver
   * `../brazo.ts`, que tiene esa medición escrita). `useLocation()` no tiene esa
   * propiedad.
   *
   * ⭐ `R2-UI2` cambia de quién es la verdad: la vista se **DERIVA** de este prop
   * (`mostrandoFormulario = props?.abrirFormulario === true`) y ya NO es estado
   * local. Antes «Nuevo envío» y «Cancelar» tocaban `useState` y no navegaban; hoy
   * llaman a `navigate("nuevo")` / `navigate("..")`, así que el botón Atrás del
   * navegador y el renglón del menú significan lo mismo que el botón. Es lo que
   * hace verificable `R2-UI2` («navegación sin navegar» = no se llama a
   * `navigate`): un estado local no deja rastro y la dirección sí.
   */
  readonly abrirFormulario?: boolean
}): JSX.Element {
  const borde = useBordeEnvios()

  /**
   * ⭐ `R1.1-F4-CA-66`/`CA-79` · el brazo de la DIRECCIÓN, para la leyenda y el
   * aviso de Nuevo envío. Es lectura y nada más: esta pantalla **no manda el
   * brazo en ningún pedido** —el servidor lo resuelve— y eso no cambia acá.
   */
  const brazoDeLaRuta = useBrazo()
  const brazo: Brazo | null = esBrazo(brazoDeLaRuta) ? brazoDeLaRuta : null

  const [page, setPage] = useState(1)
  const [items, setItems] = useState<readonly DispatchRow[]>([])
  const [total, setTotal] = useState(0)
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [recarga, setRecarga] = useState(0)

  /**
   * ⭐⭐ `R1.1-F4-CA-78` (`PRD_R1.1.md:729`) y `R1.1-F5-CA-51` (`:870`) · **«Nuevo
   * envío» tiene DIRECCIÓN PROPIA, y entrar por ella abre el formulario.**
   *
   * ── EL HUECO QUE ESTO CIERRA (declarado el `[17/9/2026]` en `App.tsx`) ──────
   *
   * La ruta `#/brazos/<brazo>/envios/nuevo` existía y montaba esta pantalla, pero
   * caía en la LISTA: *«la dirección lleva a la pantalla correcta, pero no la
   * abre EN el formulario»*. El título seguía diciendo «Envíos», así que `CA-51`
   * —que pide «Nuevo envío»— quedaba rojo, y pegar el enlace en la barra no
   * llevaba a donde el enlace dice.
   *
   * ⭐ La reparación es de UNA línea porque la costura ya estaba: el formulario
   * siempre se mostró con este estado; lo único que faltaba era que su valor
   * INICIAL saliera de la dirección. No se parte la pantalla en dos —eso sigue
   * siendo trabajo de `AW10`/`AW14`— y lo que se gana es que la URL signifique lo
   * que dice.
   *
   * ⛔ **Y sigue siendo ESTADO y no la ruta**, a propósito: «Nuevo envío» desde el
   * botón, y «Cancelar» del formulario, no navegan. Hacer que naveguen cambiaría
   * el significado de la dirección de la lista y metería entradas en el historial
   * que `CA-85` no pide. La dirección ABRE el formulario; no lo gobierna.
   */
  /**
   * ⛔⛔⭐ **RASTRO · `R2-UI2` · 23/9/2026 — el párrafo de arriba QUEDÓ DEROGADO.**
   *
   * ~~«Nuevo envío» desde el botón y «Cancelar» del formulario NO navegan.~~ El
   * criterio de `R2-UI2` los da vuelta: «Nuevo envío» es una **pantalla propia**
   * con dirección `…/envios/nuevo`, el botón NAVEGA y «Cancelar» VUELVE a Envíos.
   * Un estado local gobernando la vista es exactamente la mutación M1 que esa
   * etapa rechaza.
   *
   * ⇒ la vista se **DERIVA** de la ruta, sin `useState` que se desincronice:
   * navegar cambia `abrirFormulario`, y este renglón lo sigue solo. Eso deja
   * imposible por construcción el defecto que el estado causaba —«Nuevo envío»
   * del menú cambiaba la dirección y la lista quedaba igual.
   */
  const navigate = useNavigate()
  const mostrandoFormulario = props?.abrirFormulario === true

  /**
   * ⛔⛔⭐ **EL RENGLÓN «NUEVO ENVÍO» DEL MENÚ CAMBIABA LA DIRECCIÓN Y DEJABA LA
   * PANTALLA IGUAL. MEDIDO, DETERMINISTA, EN NAVEGADOR AISLADO.**
   *
   * ── EL SÍNTOMA ────────────────────────────────────────────────────────────
   *
   * | camino                                   | hash resultante | lo que se veía |
   * |------------------------------------------|-----------------|----------------|
   * | carga fresca de `/envios`                | `envios`        | la lista ✓     |
   * | **clic en «Nuevo envío» del menú**       | `envios/nuevo`  | **la lista** ✗ |
   * | carga fresca de `/envios/nuevo`          | `envios/nuevo`  | el formulario ✓|
   * | **clic en «Envíos» desde el formulario** | `envios`        | **el form.** ✗ |
   *
   * El operador apretaba «Nuevo envío» y **no pasaba nada visible**.
   *
   * ── LA CAUSA ──────────────────────────────────────────────────────────────
   *
   * `useState(inicial)` lee su argumento **sólo en el primer montaje**. El router
   * mantiene esta pantalla montada entre `/envios` y `/envios/nuevo` —es el mismo
   * componente—, así que cuando `abrirFormulario` pasa de `false` a `true`, el
   * `useState` de arriba lo **ignora**. La ruta cambiaba; el estado no.
   *
   * ⇒ el párrafo de arriba decía *«entrar por ella abre el formulario»*, y eso era
   * cierto **sólo para la primera entrada**. Para el resto de la sesión, no.
   *
   * ── ⛔ POR QUÉ NINGÚN EXAMEN LO VIO, Y ES LO MÁS CARO DE ESTE DEFECTO ──────
   *
   * `R1.1-F4-CA-78` y `R1.1-F5-CA-51` **miden la carga fresca**, que es
   * exactamente el camino que SÍ andaba. Un caso que monta directo en
   * `envios/nuevo` **no puede ver** un defecto que sólo existe al NAVEGAR.
   * **El criterio estaba verde y la función estaba rota al mismo tiempo.**
   * Por eso el caso que acompaña a esta reparación —`auditoria-visual-impeccable`
   * §⑦— entra por la lista y **aprieta el renglón del menú**, y hace la vuelta.
   *
   * ── ⛔ LO QUE ESTA REPARACIÓN **YA NO** SOSTIENE · DEROGADO POR `R2-UI2` ────
   *
   * ⛔⛔ Hasta `R2-UI2` este bloque afirmaba, con autoridad, que **«Nuevo envío»
   * desde el BOTÓN y «Cancelar» del formulario NO navegan**, y describía el
   * mecanismo como un efecto que sincronizaba un estado local con el **cambio** de
   * `abrirFormulario`. **Las dos cosas quedaron derogadas y el código ya no las
   * hace**: no hay ningún `useEffect` sobre `mostrandoFormulario` —lo único que
   * queda de aquel efecto es el `RASTRO · R2-UI2` de acá abajo—.
   *
   * Por qué se dio vuelta: un estado local gobernando la vista es la mutación M1
   * que `R2-UI2` rechaza, y era la causa medida del defecto de arriba —el renglón
   * del menú cambiaba la dirección y dejaba la lista igual—. Hoy la vista se
   * **DERIVA** (`mostrandoFormulario = props?.abrirFormulario === true`, arriba) y
   * los tres caminos NAVEGAN: el botón de la lista `navigate("nuevo")`, y
   * «Cancelar» / `onCreado` `navigate("..", { relative: "path" })`.
   *
   * ⛔ **No «re-sincronizar» con `useState` + `useEffect`.** Eso reintroduce el
   * defecto que este bloque documenta y pone rojo `auditoria-visual-impeccable` §⑦,
   * que entra por la LISTA, aprieta el renglón del menú y hace la vuelta —un caso
   * que ningún montaje fresco puede ver, porque el montaje fresco es justo el estado
   * en que el defecto no existe.
   */
  /*
   * ⛔ RASTRO · `R2-UI2` — acá vivía `setMostrandoFormulario(abrirPorLaRuta)`
   * dentro de un efecto, sincronizando el estado local con la ruta. Ya no hace
   * falta: la vista se deriva (arriba), así que no hay nada que sincronizar.
   */

  /**
   * ⛔ `guard` ⑤ · el detalle es `DispatchDetail` y no `DispatchRow`: los dos
   * desgloses por motivo (`failuresByReason`, `exclusionsByReason`) viajan SÓLO
   * en el detalle (`R2-CDA1`), y pedirlos desde la lista sería el N+1 que el
   * contrato evita a propósito.
   */
  const [detalle, setDetalle] = useState<DispatchDetail | null>(null)
  const [detalleCargando, setDetalleCargando] = useState(false)
  const [detalleError, setDetalleError] = useState<string | null>(null)
  const [detalleInexistente, setDetalleInexistente] = useState(false)

  const [errorCancelar, setErrorCancelar] = useState<string | null>(null)
  /**
   * ⭐ `R1.1-F4-CA-72`…`CA-77` (`PRD_R1.1.md:723-728`) · **cancelar un envío pide
   * confirmación.**
   *
   * ⛔ Guarda la FILA y no sólo el `id`, porque el popup tiene que decir el nombre
   * del envío y cuántos destinatarios tiene (`CA-73`). Sacarlos después buscando
   * el `id` en `items` sería leer dos veces la misma lista, y el día que la lista
   * se refresque con el popup abierto el texto hablaría de otro envío.
   *
   * ⛔⛔ Y es lo que hace cumplible *«sin confirmar no pasa nada»* (`CA-75`,
   * `CA-76`): el pedido sale **únicamente** desde el botón de confirmar. Cancelar
   * un envío programado es irreversible —`ADR-0090`: un envío cancelado no se
   * reactiva— así que un clic de más no se puede deshacer.
   */
  const [cancelando, setCancelando] = useState<DispatchRow | null>(null)

  /**
   * ⭐ `R2-UI2` / `CA-47` · **el aviso de creación, y por eso es ESTADO y no un
   * `navigate` pelado.** El formulario vive en la MISMA pantalla (`abrirFormulario`
   * deriva la vista), así que al volver a Envíos este componente sigue montado: el
   * aviso sobrevive a `navigate("..")`. El nombre y el `audienceSize` salen de la
   * respuesta del servidor (`creado`), no de lo que la pantalla contó.
   */
  const [avisoCreado, setAvisoCreado] = useState<{
    readonly name: string
    readonly audienceSize: number
  } | null>(null)

  /**
   * Cada cambio de página es una CONSULTA NUEVA al borde (`guard` ⑤). La bandera
   * `cancelado` del cleanup descarta la respuesta de una consulta que quedó vieja.
   */
  useEffect(() => {
    let cancelado = false
    setCargando(true)
    setError(null)

    void borde
      .listar({ page, pageSize: ENVIOS_POR_PAGINA })
      .then((resultado: DispatchListPage) => {
        if (cancelado) return
        setItems(resultado.items)
        setTotal(resultado.total)
        setCargando(false)
      })
      .catch((causa: unknown) => {
        if (cancelado) return
        setError(causa instanceof Error ? causa.message : String(causa))
        setCargando(false)
      })

    return () => {
      cancelado = true
    }
  }, [borde, page, recarga])

  /*
   * ⭐ RASTRO · `AW31` · 28/8/2026 — acá se calculaba `pageCount` a mano, una de
   * seis copias de la misma cuenta. La hace la primitiva `src/paginado.tsx`, que
   * recibe `total` y `tamanoDePagina` y NO un número de páginas ya calculado:
   * así el `guard` de «el total sale de la BASE, nunca de `items.length`» deja de
   * depender de que cada pantalla lo escriba bien por su cuenta.
   */

  const refrescar = () => {
    setRecarga((valor) => valor + 1)
  }

  /** `guard` ⑤ — el detalle se pide FRESCO cada vez, nunca memoizado. */
  const abrirDetalle = async (id: DispatchId) => {
    setDetalle(null)
    setDetalleError(null)
    setDetalleInexistente(false)
    setDetalleCargando(true)

    try {
      const fila = await borde.detalle(id)
      if (fila === null) {
        setDetalleInexistente(true)
      } else {
        setDetalle(fila)
        // `R2-UI18` / ADR-0374 §1 (ampliado): la fila del MISMO envío toma la fila
        // entera que el detalle acaba de traer —estado, motivo y conteos—. Se copia
        // un dato ya leído: ningún pedido nuevo a la lista, ningún refresco. Se busca
        // por `dispatch.id`, nunca por posición; si la fila ya no está (otra página),
        // no se agrega nada.
        setItems((actuales) => conLaFilaDelDetalle(actuales, id, fila))
      }
    } catch (causa: unknown) {
      setDetalleError(causa instanceof Error ? causa.message : String(causa))
    } finally {
      setDetalleCargando(false)
    }
  }

  const cerrarDetalle = () => {
    setDetalle(null)
    setDetalleError(null)
    setDetalleInexistente(false)
    setDetalleCargando(false)
  }

  /** `guard` ④ — los dos desenlaces negativos viajan distintos y se dicen distinto. */
  const cancelar = async (id: DispatchId) => {
    setErrorCancelar(null)
    /*
     * ⛔ El popup se cierra en los DOS desenlaces. Su aviso de fallo vive arriba
     * de la tabla, con `role="alert"` y su propio estado: un segundo lugar donde
     * decir el mismo error es un segundo lugar que puede decir otra cosa.
     */
    setCancelando(null)

    try {
      const outcome = await borde.cancelar(id)
      if (outcome.cancelled) {
        refrescar()
      } else {
        setErrorCancelar(`No se pudo cancelar: ya estaba ${outcome.state}`)
      }
    } catch (causa: unknown) {
      /*
       * ⛔ El `409` NO es «no existe»: es un DESENLACE, y el estado que lo impidió
       * viaja en el cuerpo. Se lee ANTES de caer en el `404`, porque un `409`
       * tratado como `404` le dice al operador que el envío se borró cuando en
       * realidad ya se estaba enviando.
       */
      const outcome = await outcomeDelError(causa)
      if (outcome !== null) {
        setErrorCancelar(
          `No se pudo cancelar: ya estaba ${outcome.cancelled ? "cancelado" : outcome.state}`,
        )
        return
      }
      if (esErrorConStatus(causa, 404)) {
        setErrorCancelar("Ese envío no existe")
        return
      }
      setErrorCancelar(causa instanceof Error ? causa.message : String(causa))
    }
  }

  /**
   * ⛔ `guard` 3 · CA-32/33 — **«Progreso» cuenta lo TERMINADO, sin contar dos
   * veces a los entregados.**
   *
   * `enviados` ya incluye a los entregados y a los leídos
   * (⟦los-conteos-son-acumulativos⟧), así que sumarlos otra vez infla el
   * porcentaje y puede pasarse de 100. Los `excluidos` NO están en
   * `DispatchCounts` —no tienen número propio—, y por eso salen del desglose del
   * borde: sumar `exclusionsByReason` es la única forma de contarlos de verdad en
   * vez de estimarlos.
   */
  const excluidos =
    detalle === null
      ? 0
      : detalle.exclusionsByReason.reduce((suma, desglose) => suma + desglose.count, 0)

  return (
    <section
      className="envios"
      /* El `<h1 id="envios-titulo">` sólo existe en la rama de la lista; el
         formulario trae su propio título. Ausente, no `undefined`. */
      {...(mostrandoFormulario ? {} : { "aria-labelledby": "envios-titulo" })}
    >
      {/*
        ⛔⛔ **O LA LISTA O EL FORMULARIO, NUNCA LOS DOS** — y esto cambió en R1.1.

        ~~Hasta R1 el formulario se dibujaba DEBAJO de la lista, con el encabezado
        «Envíos» arriba y todo lo demás en pie.~~ Derogado por `PRD_R1.1.md:870`
        (`R1.1-F5-CA-51`), que pide que `#/brazos/<brazo>/envios/nuevo` **muestre
        el título «Nuevo envío»**, y por `PRD_R1.1.md:729` (`R1.1-F4-CA-78`), que
        le da dirección propia.

        ⭐ **Medido, y por eso se cambió:** apilados, la pantalla tenía DOS `<h1>`
        —«Envíos» primero y «Nuevo envío» después—, así que su título era
        «Envíos». El caso de `CA-51` salía rojo con el formulario ya montado y
        visible; no era un hueco de la ruta sino de la forma.

        ⚠️ El popup de cancelar queda afuera de esta rama a propósito: sólo puede
        abrirse desde una fila, o sea con la lista a la vista.
      */}
      {!mostrandoFormulario && (
        <>
        <header className="envios__encabezado">
          <h1 id="envios-titulo">Envíos</h1>
          <button type="button" onClick={() => navigate("nuevo")}>
            Nuevo envío
          </button>
        </header>

        {/*
          ⭐ `R1.1-F4-CA-66` (`PRD_R1.1.md:717`) · **la leyenda EMPIEZA nombrando la
          zona**: *«la leyenda empieza con “Envíos de KAI · Brazo Híbrido”»*. Es la
          regla 1 de `R1.1-F4` (`:595`): toda pantalla del brazo nombra el brazo en
          su leyenda o aviso.

          ⛔ Va ACÁ arriba y no de `<caption>` de la tabla, y el motivo está medido:
          la tabla **no se dibuja con el historial vacío** —abajo, `total === 0`
          tiene su propio bloque— así que una leyenda adentro de `<table>`
          desaparecería justo en la pantalla donde el operador tiene menos con qué
          orientarse. Es el mismo rojo que costó en Exportación.
        */}
        <p className="envios__leyenda">
          {brazo === null ? "Envíos." : `Envíos de ${selloDelBrazo(brazo)}.`}
        </p>

        {errorCancelar !== null && (
          <p className="envios__error" role="alert">
            {errorCancelar}
          </p>
        )}

        {error !== null && (
          <p className="envios__error" role="alert">
            No se pudieron cargar los envíos. {error}
          </p>
        )}

        {/*
          ⭐⭐ `R2-UI2` / `CA-47` · **«Envío «[nombre]» creado para N participantes.»**
          El `N` es el `audienceSize` de la RESPUESTA del servidor, no lo que la
          pantalla contó. Las comillas «» son las del PRD, textuales: son parte de
          la frase. `role="status"` y no `alert`: es una confirmación, no un error.
          `.nota` y no una clase propia: la caja de aviso que este directorio ya
          usa (`formulario.tsx`) y que tiene regla en la hoja —una clase nueva sería
          una huérfana para `r11-clases-sin-regla`—; mismo criterio que el aviso de
          `CA-72` en `padron/tabla.tsx`.
        */}
        {avisoCreado !== null && (
          <p className="nota" role="status">
            Envío «{avisoCreado.name}» creado para {avisoCreado.audienceSize} participantes.
          </p>
        )}

        {cargando && error === null && <p className="envios__vacio">Cargando envíos…</p>}

        {/* `guard` ⑥ — «no hay envíos» es un resultado, NO un fallo de lectura. */}
        {!cargando && error === null && total === 0 && (
          <div className="envios__vacio">
            <p>No hay envíos</p>
            <button type="button" onClick={() => navigate("nuevo")}>
              Nuevo envío
            </button>
          </div>
        )}

        {!cargando && error === null && total > 0 && (
          <>
            <div className="tabla-rodante">
              <table className="envios__tabla">
                <thead>
                  <tr>
                    {COLUMNAS.map((columna) => (
                      <th key={columna} scope="col">
                        {columna}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.map((fila) => (
                    <tr key={fila.dispatch.id}>
                      <td>{fila.dispatch.name}</td>
                      <td>{fila.dispatch.template.name}</td>
                      <td>
                        {fila.dispatch.audienceLabel}
                        {fila.dispatch.audienceFilterLabel !== "" && (
                          <>
                            <br />
                            {fila.dispatch.audienceFilterLabel}
                          </>
                        )}
                      </td>
                      <td>{textoDeProgramación(fila.dispatch.scheduledFor)}</td>
                      {/* `guard` ② + CA-17…20/80 — el estado rotulado y el motivo debajo. */}
                      <td>
                        {etiquetaDeEstado(fila.dispatch.state)}
                        {motivoDelFallo(fila.dispatch) !== null && (
                          /*
                           * ⛔ SIN CLASE, y no por descuido: `R1.1-F5` barre toda
                           * clase del JSX contra `src/estilos.css` y pone rojo la
                           * que no tiene regla. Acá había `envios__motivo`, que NO
                           * tiene regla en la hoja —y esta stage no puede
                           * escribirla: la hoja está fuera de su territorio—.
                           * Un `<div>` ya es bloque, así que el motivo queda
                           * DEBAJO del estado, que es lo que piden `CA-17…20`.
                           * Sacar la clase no cambia lo que se ve: sin regla, no
                           * dibujaba nada.
                           */
                          <div>{motivoDelFallo(fila.dispatch)}</div>
                        )}
                      </td>
                      {/* `guard` ③ — los tres conteos SIEMPRE; el fallido se ve acá. */}
                      <td>{textoDeConteos(fila.counts)}</td>
                      <td>
                        <button type="button" onClick={() => abrirDetalle(fila.dispatch.id)}>
                          Ver detalle
                        </button>
                        {/* `guard` ④ — Cancelar sólo en `programado`. */}
                        {fila.dispatch.state === "programado" && (
                          <button
                            type="button"
                            onClick={() => {
                              setErrorCancelar(null)
                              setCancelando(fila)
                            }}
                          >
                            Cancelar
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/*
              ⭐ RASTRO · `AW31` · 28/8/2026 — acá había una barra escrita a mano, una
              de OCHO copias en siete archivos. Ahora es la primitiva `src/paginado.tsx`,
              que hace la cuenta con `total` y el tamaño de página: el `guard` deja de
              depender de que cada pantalla lo calcule bien por su cuenta.

              ⭐ Y acá la costura (`R2-UI3`/`CA-82`): como la barra visible se fue a la
              primitiva, esta pantalla YA NO escribe los botones. Los NOMBRA igual, como
              instrucción para quien navega con lector de pantalla, sin dibujar una
              segunda barra para la misma lista.
            */}
            <p style={SOLO_PARA_LECTORES}>
              Usá Anterior y Siguiente para cambiar de página en la lista de envíos.
            </p>
            <Paginado
              etiqueta="envíos"
              pagina={page}
              total={total}
              tamanoDePagina={ENVIOS_POR_PAGINA}
              unidad={{ singular: "envío", plural: "envíos" }}
              onPagina={setPage}
            />
          </>
        )}
        </>
      )}

      {mostrandoFormulario && (
        <FormularioEnvio
          /*
           * ⭐ `R1.1-F4-CA-79` · el brazo de la RUTA entra por prop. Es el MISMO
           * valor que la leyenda de arriba (`CA-66`), leído una sola vez: dos
           * lectores del mismo parámetro de ruta son la forma de que la leyenda y
           * el aviso digan brazos distintos sin que nada se ponga rojo.
           *
           * ⛔ El `{...(… ? {brazo} : {})}` no es un adorno:
           * `exactOptionalPropertyTypes` distingue `brazo={undefined}` de la prop
           * ausente, y ausente es lo que hace que el aviso no se dibuje.
           */
          {...(brazo !== null ? { brazo } : {})}
          cargarElegibles={props?.cargarElegibles ?? SIN_FUENTE_DE_AUDIENCIA}
          cargarPlantillas={props?.cargarPlantillas ?? SIN_CATALOGO_DE_PLANTILLAS}
          /*
           * ⭐ `R2-UI2` · ~~la lista APROBADA y los lotes entran por la misma puerta
           * que el brazo: `{...(… ? {x} : {})}` porque con
           * `exactOptionalPropertyTypes` una prop con `undefined` NO es lo mismo
           * que una prop ausente, y ausente es lo que hace que el selector no
           * ofrezca nada en vez de mostrar «no hay lista».~~
           *
           * ⛔⭐ **RASTRO · 24/9/2026, `R2-UI11`.** Las dos props son obligatorias
           * en esta pantalla, así que siempre llegan y se pasan DIRECTAS: el
           * spread condicional ya no describía nada (no hay «ausente» que
           * preservar). Y no es «la lista APROBADA»: es la lista entera del
           * brazo; qué se ofrece lo decide el formulario (ver el docstring de
           * `listaParaEnvios` arriba). La rama «no hay lista» del formulario
           * sigue existiendo para quien lo monte sin la prop, pero desde esta
           * pantalla ya no se alcanza.
           */
          listaParaEnvios={props.listaParaEnvios}
          lotesDe={props.lotesDe}
          /*
           * ⛔ `relative: "path"`: `envios` y `envios/nuevo` son rutas HERMANAS
           * bajo `brazos/:brazo` (`App.tsx`), y el `".."` por defecto de
           * `react-router` sube un nivel de RUTA —a `#/brazos/<brazo>`, fuera de
           * Envíos, con el aviso perdido—. Por dirección, sube un segmento.
           */
          onCreado={(creado) => {
            setAvisoCreado({ name: creado.name, audienceSize: creado.audienceSize })
            refrescar()
            navigate("..", { relative: "path" })
          }}
          onCancelar={() => navigate("..", { relative: "path" })}
        />
      )}

      {/*
        ⛔⭐ `R1.1-F4-CA-72`…`CA-77` · **EL POPUP DE CANCELAR UN ENVÍO.**

        Va acá, fuera de la tabla, y no adentro del `<td>` de la fila: la lista se
        refresca sola (`refrescar()` tras cancelar) y un diálogo montado dentro de
        una fila **se desmonta con ella**. El padrón y el Banco pueden tenerlo en
        la celda porque sus filas no se reordenan solas; ésta sí.

        ⚠️ Y por eso el estado guarda la FILA y no el `id`: el texto del popup
        sigue siendo el del envío que el operador eligió aunque la lista cambie
        debajo.
      */}
      {cancelando !== null && (
        <Dialogo titulo="Cancelar envío" onCerrar={() => setCancelando(null)}>
          {/*
            `CA-73` · el nombre del envío y cuántos destinatarios se quedan sin él.
            ⛔ Las comillas curvas son las del PRD, textual: son parte de la frase,
            no de cómo el criterio la cita.
          */}
          <p>
            “{cancelando.dispatch.name}” no se va a enviar a sus{" "}
            {cancelando.dispatch.audienceSize} destinatarios.
          </p>
          {/*
            `CA-74` · a quién afecta. Con los dos brazos teniendo envíos con
            nombres parecidos —«Envío septiembre Híbrido» y «… Puro»— es lo único
            que distingue cuál se está por cancelar.
          */}
          {brazo !== null && <p>Afecta solo a {selloDelBrazo(brazo)}.</p>}

          <footer className="dialogo__acciones">
            {/*
              ⛔ La salida se llama «No cancelar» y no «Cancelar» (`CA-75`), y no
              es un capricho del PRD: en un popup que cancela algo, un botón
              «Cancelar» significa las dos cosas opuestas a la vez.
            */}
            <button type="button" className="boton" onClick={() => setCancelando(null)}>
              No cancelar
            </button>
            {/* `R1.1-F5-CA-96` · el que confirma es el sólido en `--peligro`. */}
            <button
              type="button"
              className="boton boton--peligro-solido"
              onClick={() => {
                void cancelar(cancelando.dispatch.id)
              }}
            >
              Sí, cancelar envío
            </button>
          </footer>
        </Dialogo>
      )}

      {detalleCargando && <p className="envios__vacio">Cargando detalle…</p>}

      {detalleError !== null && (
        <p className="envios__error" role="alert">
          {detalleError}
        </p>
      )}

      {detalleInexistente && (
        <p className="envios__error" role="alert">
          Ese envío no existe
        </p>
      )}

      {detalle !== null && (
        <div className="envios__detalle">
          <h2>{detalle.dispatch.name}</h2>
          <p>Plantilla: {detalle.dispatch.template.name}</p>
          <p>
            Audiencia: {detalle.dispatch.audienceLabel}
            {detalle.dispatch.audienceFilterLabel !== "" && (
              <>
                <br />
                {detalle.dispatch.audienceFilterLabel}
              </>
            )}
          </p>
          <p>Programación: {textoDeProgramación(detalle.dispatch.scheduledFor)}</p>
          <p>
            Estado: {etiquetaDeEstado(detalle.dispatch.state)}
            {motivoDelFallo(detalle.dispatch) !== null && <> — {motivoDelFallo(detalle.dispatch)}</>}
          </p>
          {/* El progreso NO es un campo: se deriva de conteos contra audienceSize. */}
          {detalle.dispatch.state === "enviando" && detalle.dispatch.audienceSize > 0 && (
            <p>
              Progreso:{" "}
              {Math.round(
                ((detalle.counts.enviados + detalle.counts.fallidos + excluidos) /
                  detalle.dispatch.audienceSize) *
                  100,
              )}
              %
            </p>
          )}
          {/* `A-P0-4` — los conteos pueden cambiar: se muestran y no se tratan como finales. */}
          <p>Conteos: {textoDeConteos(detalle.counts)}</p>
          {/*
            ⛔ CA-35/36/37 · los dos desgloses, y **condicionales**: sin elementos
            no se dibuja ni el título. El ORDEN (mayor a menor) lo da el borde:
            reordenar acá sería una segunda regla que puede discrepar de la
            primera, así que la lista se muestra tal como llega, sin copiarla.
            ⛔ **El `<div>` va SIN CLASE a propósito.** Tenía `envios__desglose`,
            que NO tiene regla en `src/estilos.css`, y `R1.1-F5` pone rojo toda
            clase del JSX sin regla. La regla no se puede escribir desde acá —la
            hoja está fuera del territorio de esta stage—, así que se saca la
            clase muerta: un `<div>` ya agrupa en bloque, y el `<h3>`/`<ul>` de
            adentro traen su propia voz. Sacarla NO cambia lo que se ve: sin
            regla, la clase no dibujaba nada.
          */}
          {detalle.failuresByReason.length > 0 && (
            <div>
              <h3>Fallidos, por motivo</h3>
              <ul>
                {detalle.failuresByReason.map(({ reason, count }) => (
                  <li key={reason}>
                    {reason}: {count}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {detalle.exclusionsByReason.length > 0 && (
            <div>
              <h3>Excluidos, por motivo</h3>
              <ul>
                {detalle.exclusionsByReason.map(({ reason, count }) => (
                  <li key={reason}>
                    {reason}: {count}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {/*
            ⛔ `R2-UI3`/`A11` · «Volver a Envíos» y NO «Cerrar»: el detalle no es
            un popup que se cierra, es un lugar del que se vuelve. El gate rechaza
            el literal `Cerrar`.
          */}
          <button type="button" onClick={cerrarDetalle}>
            Volver a Envíos
          </button>
        </div>
      )}
    </section>
  )
}
